import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  updateDoc
} from 'firebase/firestore';

function ListAgents() {
  const [agents, setAgents] = useState([]);
  const [filteredAgents, setFilteredAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedAgent, setSelectedAgent] = useState(null); // 🎯 State for Modal Popup

  // 🎯 Location Edit States
  const [locationInput, setLocationInput] = useState('');
  const [isSavingLocation, setIsSavingLocation] = useState(false);

  const rowsPerPage = 50;

  useEffect(() => {
    const q = query(
      collection(db, 'agents'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((docSnapshot) => ({
        id: docSnapshot.id,
        ...docSnapshot.data()
      }));

      setAgents(data);
      setFilteredAgents(data);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Update location input state whenever selected agent changes
  useEffect(() => {
    if (selectedAgent) {
      setLocationInput(selectedAgent.location || '');
    }
  }, [selectedAgent]);

  useEffect(() => {
    let result = agents;

    // Search
    if (searchTerm) {
      result = result.filter((agent) =>
        (agent.agentName || '')
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||

        (agent.companyName || '')
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||

        (agent.phone || '')
          .toString()
          .includes(searchTerm)
      );
    }

    // Date Filter
    if (dateFilter) {
      result = result.filter((agent) => {
        const createdDate =
          agent.createdAt?.toDate()?.toISOString()?.split('T')[0];

        return createdDate === dateFilter;
      });
    }

    setFilteredAgents(result);
    setCurrentPage(1);
  }, [searchTerm, dateFilter, agents]);

  // 🎯 Function to Save / Update Location in Firestore
  const handleSaveLocation = async () => {
    if (!selectedAgent) return;

    const trimmedLocation = locationInput.trim();
    if (!trimmedLocation) {
      alert("Please enter a valid location.");
      return;
    }

    setIsSavingLocation(true);

    try {
      const agentRef = doc(db, 'agents', selectedAgent.id);
      await updateDoc(agentRef, {
        location: trimmedLocation,
        locationUpdatedAt: new Date()
      });

      setSelectedAgent((prev) => ({
        ...prev,
        location: trimmedLocation
      }));

      alert(`Location updated successfully to "${trimmedLocation}"!`);
    } catch (error) {
      console.error("Error updating location:", error);
      alert("Failed to update location. Please try again.");
    } finally {
      setIsSavingLocation(false);
    }
  };

  const totalPages = Math.ceil(
    filteredAgents.length / rowsPerPage
  );

  const startIndex = (currentPage - 1) * rowsPerPage;

  const paginatedAgents = filteredAgents.slice(
    startIndex,
    startIndex + rowsPerPage
  );

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  if (loading) {
    return (
      <div className="admin-loader">
        Loading Agents...
      </div>
    );
  }

  return (
    <div className="orders-container">

      {/* Search & Filter */}
      <div className="filter-bar">
        <div className="search-box">
          <i className="fas fa-search"></i>

          <input
            type="text"
            placeholder="Search by Agent Name, Company or Phone..."
            value={searchTerm}
            onChange={(e) =>
              setSearchTerm(e.target.value)
            }
          />
        </div>

        <div className="filter-options">
          <input
            type="date"
            value={dateFilter}
            onChange={(e) =>
              setDateFilter(e.target.value)
            }
          />

          {(searchTerm || dateFilter) && (
            <button
              className="clear-btn"
              onClick={() => {
                setSearchTerm('');
                setDateFilter('');
              }}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="admin-table-wrapper">
        <table className="admin-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Agent Name</th>
              <th>Company Name</th>
              <th>Phone Number</th>
              <th>Location</th>
              <th>Registered Date</th>
              <th>Status</th>
              <th>Notify</th>
            </tr>
          </thead>

          <tbody>
            {paginatedAgents.length > 0 ? (
              paginatedAgents.map((agent, index) => (
                <tr 
                  key={agent.id} 
                  onClick={() => setSelectedAgent(agent)} 
                  style={{ cursor: 'pointer' }}
                  className="table-row-hover"
                >
                  <td>
                    {startIndex + index + 1}
                  </td>

                  <td>
                    <strong>
                      {agent.agentName || '-'}
                    </strong>
                  </td>

                  <td>
                    {agent.companyName || '-'}
                  </td>

                  <td>{agent.phone || '-'}</td>

                  <td>
                    {agent.location || '-'}
                  </td>

                  <td>
                    {agent.createdAt?.toDate()
                      ?.toLocaleDateString() || '-'}
                  </td>

                  <td>
                    <span
                      className={`status-badge ${
                        agent.status
                          ?.toLowerCase()
                          ?.replace(/\s/g, '-') ||
                        'active'
                      }`}
                    >
                      {agent.status || 'Active'}
                    </span>
                  </td>

                  <td>
                    <button
                      className="view-btn-icon"
                      title="Send Notification"
                      onClick={(e) => {
                        e.stopPropagation(); // 🎯 Prevent Row Click Event
                        alert(`Send notification to ${agent.agentName}`);
                      }}
                    >
                      <i className="fas fa-bell"></i>
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="8"
                  style={{
                    textAlign: 'center',
                    padding: '30px'
                  }}
                >
                  No agents found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '10px',
            marginTop: '20px'
          }}
        >
          <button
            className="clear-btn"
            disabled={currentPage === 1}
            onClick={() =>
              setCurrentPage((prev) => prev - 1)
            }
          >
            Previous
          </button>

          <span
            style={{
              padding: '10px',
              fontWeight: '600'
            }}
          >
            Page {currentPage} of {totalPages}
          </span>

          <button
            className="clear-btn"
            disabled={currentPage === totalPages}
            onClick={() =>
              setCurrentPage((prev) => prev + 1)
            }
          >
            Next
          </button>
        </div>
      )}

      {/* 🎯 FULL DETAILS POPUP MODAL */}
      {selectedAgent && (
        <div className="modal-overlay" onClick={() => setSelectedAgent(null)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            
            {/* Modal Header */}
            <div className="modal-header">
              <div>
                <h2>{selectedAgent.agentName || 'Agent Details'}</h2>
                <span className={`status-badge ${selectedAgent.status?.toLowerCase() || 'active'}`}>
                  {selectedAgent.status || 'Active'}
                </span>
              </div>
              <button className="modal-close-btn" onClick={() => setSelectedAgent(null)}>
                &times;
              </button>
            </div>

            {/* Modal Body */}
            <div className="modal-body">

              {/* 🎯 LOCATION EDIT SECTION */}
              <div className="modal-section" style={{ backgroundColor: '#fff9e6', padding: '15px', borderRadius: '10px', border: '1px solid #ffe58f', marginBottom: '15px' }}>
                <h3 style={{ color: '#b78103', marginBottom: '10px' }}>
                  <i className="fas fa-map-marker-alt"></i> Edit Agent Location
                </h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '13px', fontWeight: 'bold', color: '#333' }}>
                      Agent Location / City
                    </label>
                    <input
                      type="text"
                      value={locationInput}
                      onChange={(e) => setLocationInput(e.target.value)}
                      placeholder="Enter location e.g. Dubai, Sharjah"
                      style={{
                        width: '100%',
                        padding: '10px',
                        borderRadius: '6px',
                        border: '1px solid #ccc',
                        marginTop: '4px',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    />
                  </div>
                  <button
                    onClick={handleSaveLocation}
                    disabled={isSavingLocation}
                    style={{
                      backgroundColor: '#d48806',
                      color: '#fff',
                      border: 'none',
                      padding: '11px 20px',
                      borderRadius: '6px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      marginTop: '20px'
                    }}
                  >
                    {isSavingLocation ? 'Saving...' : 'Update Location'}
                  </button>
                </div>
              </div>

              {/* Basic Information */}
              <div className="modal-section">
                <h3><i className="fas fa-id-card"></i> Basic Details</h3>
                <div className="details-grid">
                  <div><strong>Company Name:</strong> {selectedAgent.companyName || '-'}</div>
                  <div><strong>Phone Number:</strong> {selectedAgent.phone || '-'}</div>
                  <div><strong>Current Location:</strong> {selectedAgent.location || '-'}</div>
                  <div><strong>Registered On:</strong> {selectedAgent.createdAt?.toDate()?.toLocaleString() || '-'}</div>
                  <div className="full-width">
                    <strong>Pickup Areas:</strong> {selectedAgent.pickupAreas || '-'}
                  </div>
                </div>
              </div>

              {/* Bank Details */}
              <div className="modal-section">
                <h3><i className="fas fa-university"></i> Bank Details</h3>
                {selectedAgent.bankDetails ? (
                  <div className="details-grid">
                    <div><strong>Bank Name:</strong> {selectedAgent.bankDetails.bankName || '-'}</div>
                    <div><strong>Account Name:</strong> {selectedAgent.bankDetails.accountName || '-'}</div>
                    <div><strong>Account Number:</strong> {selectedAgent.bankDetails.accountNumber || '-'}</div>
                    <div><strong>IBAN:</strong> {selectedAgent.bankDetails.iban || '-'}</div>
                  </div>
                ) : (
                  <p className="no-data-text">No bank details provided.</p>
                )}
              </div>

              {/* Services Offered */}
              <div className="modal-section">
                <h3><i className="fas fa-concierge-bell"></i> Services Offered</h3>
                {selectedAgent.servicesOffered && selectedAgent.servicesOffered.length > 0 ? (
                  <div className="modal-table-wrapper">
                    <table className="modal-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Service</th>
                          <th>Item</th>
                          <th>Rate (AED)</th>
                          <th>Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedAgent.servicesOffered.map((srv, idx) => (
                          <tr key={idx}>
                            <td>{idx + 1}</td>
                            <td><strong>{srv.service || '-'}</strong></td>
                            <td>{srv.item || '-'}</td>
                            <td>AED {srv.rate || '0'}</td>
                            <td>{srv.type || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="no-data-text">No services listed.</p>
                )}
              </div>

              {/* Weekly Working Hours */}
              <div className="modal-section">
                <h3><i className="fas fa-clock"></i> Weekly Working Hours</h3>
                {selectedAgent.weeklyWorkingHours ? (
                  <div className="working-hours-grid">
                    {daysOfWeek.map((day) => {
                      const daySchedule = selectedAgent.weeklyWorkingHours[day];
                      return (
                        <div key={day} className={`day-card ${daySchedule?.enabled ? 'enabled' : 'disabled'}`}>
                          <div className="day-name">{day}</div>
                          {daySchedule?.enabled ? (
                            <div className="day-time">
                              {daySchedule.open} - {daySchedule.close}
                            </div>
                          ) : (
                            <div className="day-closed">Closed</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="no-data-text">No working hours configured.</p>
                )}
              </div>

            </div>

            {/* Modal Footer */}
            <div className="modal-footer">
              <button className="clear-btn" onClick={() => setSelectedAgent(null)}>
                Close
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}

export default ListAgents;